import React, { useEffect, useState } from 'react';
import { UseFormMethods } from 'react-hook-form';
import BatteryStorage from 'src/models/contract_types/BatteryStorage';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import IndexReferenceType from 'src/models/IndexReferenceType';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';

interface IBatteryStorageGenerationProps extends Partial<Pick<UseFormMethods, 'getValues' | 'register' | 'errors' | 'setValue'>> {
    batteryStorageData: BatteryStorage;
    measurementTypes: Array<IndexReferenceType>;
    isInHanded: boolean;
    totalNet: number;
    calculateTotal?: (measurementTypeId: number, paymentAmount: number, grossAcres: number, measurementTypesList: Array<IndexReferenceType>) => void;
}

const BatteryStorageGeneration = (props: IBatteryStorageGenerationProps) => {
    const [isConstructionPeriod, setIsConstructionPeriod] = useState<boolean>(false);
    const [isConstructionExtensionPeriod, setIsConstructionExtensionPeriod] = useState<boolean>(false);
    const [isEasementAccess, setIsEasementAccess] = useState<boolean>(false);
    const [isEasementTransmission, setIsEasementTransmission] = useState<boolean>(false);
    const [isTemporaryUse, setIsTemporaryUse] = useState<boolean>(false);
    const [isCropBuyout, setIsCropBuyout] = useState<boolean>(false);
    const [isDevelopmentPeriod, setIsDevelopmentPeriod] = useState<boolean>(false);
    const [isDevelopmentPeriodExtension, setIsDevelopmentPeriodExtension] = useState<boolean>(false);
    const [isDevelopmentPeriodSecondExtension, setIsDevelopmentPeriodSecondExtension] = useState<boolean>(false);
    const [isOperationsPeriod, setIsOperationsPeriod] = useState<boolean>(false);
    const [isExtendedOperationsPeriod, setIsExtendedOperationsPeriod] = useState<boolean>(false);
    const [isRestorationPeriod, setIsRestorationPeriod] = useState<boolean>(false);
    const [isSubstationArea, setIsSubstationArea] = useState<boolean>(false);

    useEffect(() => {
        if (!!props.batteryStorageData && props.batteryStorageData.batteryStorageHolds?.length > 0) {
            onUpdateFields(true);
        }
    }, [props.batteryStorageData]);

    const getOptions = (type: string, item: string = '') => {
        if (props.measurementTypes?.length > 0) {
            return props.measurementTypes
                .filter((mt) => mt.description === type)
                .map((option) => {
                    if (type === 'Fee' && option.name === 'Per Item') {
                        return { value: option.id, label: `Per ${item}` };
                    }
                    return { value: option.id, label: option.name };
                });
        }
        return [];
    };

    const onUpdateFields = (initialize: boolean) => {
        if (initialize === true) {
            setIsConstructionPeriod(props.batteryStorageData.batteryStorageHolds[0].constructionPeriod === true);
            setIsConstructionExtensionPeriod(props.batteryStorageData.batteryStorageHolds[0].constructionExtensionPeriod === true);
            setIsEasementAccess(props.batteryStorageData.batteryStorageHolds[0].accessEasement === true);
            setIsEasementTransmission(props.batteryStorageData.batteryStorageHolds[0].transmissionEasement === true);
            setIsTemporaryUse(props.batteryStorageData.batteryStorageHolds[0].temporaryUseAreas === true);
            setIsCropBuyout(props.batteryStorageData.batteryStorageHolds[0].cropBuyout === true);
            setIsDevelopmentPeriod(props.batteryStorageData.batteryStorageHolds[0].developmentPeriod === true);
            setIsDevelopmentPeriodExtension(props.batteryStorageData.batteryStorageHolds[0].developmentPeriodExtension === true);
            setIsDevelopmentPeriodSecondExtension(props.batteryStorageData.batteryStorageHolds[0].developmentPeriodSecondExtension === true);
            setIsOperationsPeriod(props.batteryStorageData.batteryStorageHolds[0].operationsPeriod === true);
            setIsExtendedOperationsPeriod(props.batteryStorageData.batteryStorageHolds[0].extendedOperationsPeriod === true);
            setIsRestorationPeriod(props.batteryStorageData.batteryStorageHolds[0].restorationPeriod);
            setIsSubstationArea(props.batteryStorageData.batteryStorageHolds[0].substationArea);
        } else {
            let ref = props.getValues();
            if (!!ref.batteryStorageHolds?.[0]) {
                setIsConstructionPeriod(ref.batteryStorageHolds[0].constructionPeriod === true);
                setIsConstructionExtensionPeriod(ref.batteryStorageHolds[0].constructionExtensionPeriod === true);
                setIsEasementAccess(ref.batteryStorageHolds[0].accessEasement === true);
                setIsEasementTransmission(ref.batteryStorageHolds[0].transmissionEasement === true);
                setIsTemporaryUse(ref.batteryStorageHolds[0].temporaryUseAreas === true);
                setIsCropBuyout(ref.batteryStorageHolds[0].cropBuyout === true);
                setIsDevelopmentPeriod(ref.batteryStorageHolds[0].developmentPeriod === true);
                setIsDevelopmentPeriodExtension(ref.batteryStorageHolds[0].developmentPeriodExtension === true);
                setIsDevelopmentPeriodSecondExtension(ref.batteryStorageHolds[0].developmentPeriodSecondExtension === true);
                setIsOperationsPeriod(ref.batteryStorageHolds[0].operationsPeriod === true);
                setIsExtendedOperationsPeriod(ref.batteryStorageHolds[0].extendedOperationsPeriod === true);
                setIsRestorationPeriod(ref.batteryStorageHolds[0].restorationPeriod);
                setIsSubstationArea(ref.batteryStorageHolds[0].substationArea);
            }
        }
    };

    const handleTotals = () => {
        let rep = props.getValues();
        let netAcres = props.totalNet ? props.totalNet : 0;

        let executionBonusTypeId = parseInt(rep.batteryStorageHolds[0].executionBonusTypeId, 10);
        let executionBonus = parseFloat(rep.batteryStorageHolds[0].executionBonus);
        if (!isNaN(executionBonusTypeId) && !isNaN(executionBonus)) {
            let executionBonusTotal = props.calculateTotal(executionBonusTypeId, executionBonus, netAcres, props.measurementTypes);
            props.setValue('batteryStorageHolds[0].executionBonusTotal', executionBonusTotal);
        }

        let substationFeeTypeId = parseInt(rep.batteryStorageHolds[0].substationFeeTypeId, 10);
        let substationFee = parseFloat(rep.batteryStorageHolds[0].substationFee);
        let substationAcres = parseFloat(rep.batteryStorageHolds[0].substationAcres);
        if (!isNaN(substationFeeTypeId) && !isNaN(substationFee)) {
            let substationFeeTotal = props.calculateTotal(substationFeeTypeId, substationFee, substationAcres, props.measurementTypes);
            props.setValue('batteryStorageHolds[0].substationFeeTotal', substationFeeTotal);
        }
    };

    return (
        <div>
            <div style={{ margin: '1rem', }}>
                <div style={{ height: '100%' }}>
                    <div id="formContent">
                        <div>
                            <div className="form-row">
                                <RHFInputBox
                                    label="Battery Storage Name"
                                    name="contractName"
                                    register={props.register}
                                    errors={props.errors}
                                    isRequired={true}
                                    disabled={props.isInHanded}
                                    bodyStyle={{ width: '50%' }}
                                    style={{ width: '100%' }}
                                />
                                <RHFInputBox
                                    label="DLC Number"
                                    name="dlcLeaseNumber"
                                    register={props.register}
                                    errors={props.errors}
                                    disabled={true}
                                    bodyStyle={{ width: '50%', marginRight: '1rem' }}
                                    style={{ width: '100%' }}
                                />
                            </div>
                            <div className="form-row">
                                <RHFInputBox
                                    label="Lease Date"
                                    name="leaseDate"
                                    register={props.register}
                                    type="date"
                                    errors={props.errors}
                                    rules={{ required: 'field required' }}
                                    isRequired={true}
                                    disabled={props.isInHanded}
                                    bodyStyle={{ width: '50%' }}
                                    style={{ width: '100%' }} 
                                />

                                <RHFInputBox
                                    label="Effective Date"
                                    name="effectiveDate"
                                    register={props.register}
                                    type="date"
                                    errors={props.errors}
                                    rules={{ required: 'field required' }}
                                    isRequired={true}
                                    disabled={props.isInHanded}
                                    bodyStyle={{ width: '50%', marginRight: '1rem' }}
                                    style={{ width: '100%' }} 
                                />
                            </div>
                            <div className="form-row">
                                <RHFSelect
                                    name="recordTypeId"
                                    label="Record Type"
                                    options={[
                                        { value: 1, label: 'Book/Volume' },
                                        { value: 2, label: 'Instrument #' },
                                    ]}
                                    register={props.register}
                                    errors={props.errors}
                                    bodyStyle={{ width: '20%' }}
                                    style={{ width: '100%' }}
                                />
                                <RHFInputBox 
                                    label="Book/Volume" 
                                    name="volume" 
                                    register={props.register} 
                                    type="number" 
                                    errors={props.errors} 
                                    bodyStyle={{ width: '20%' }}
                                    style={{ width: '100%' }}
                                />
                                <RHFInputBox 
                                    label="Page" 
                                    name="page" 
                                    register={props.register} 
                                    type="number" 
                                    errors={props.errors} 
                                    bodyStyle={{ width: '20%' }}
                                    style={{ width: '100%' }}
                                />
                                <RHFInputBox
                                    label="Instrument Number"
                                    name="instrumentNumber"
                                    register={props.register}
                                    type="number"
                                    errors={props.errors}
                                    bodyStyle={{ width: '20%' }}
                                    style={{ width: '100%' }}
                                />
                                <RHFInputBox 
                                    label="Recording Date" 
                                    name="recordDate" 
                                    register={props.register} 
                                    type="date" 
                                    errors={props.errors}
                                    bodyStyle={{ width: '20%' }}
                                    style={{ width: '100%' }} 
                                />
                                <RHFCheckbox 
                                    name="isFiledByMemo" 
                                    label="Filed By Memorandum" 
                                    register={props.register} 
                                    errors={props.errors} 
                                    bodyStyle={{ width: '20%', marginTop: '1.5rem', marginRight: '1rem' }}
                                />
                            </div>

                            <div className="form-row">
                                <RHFInputBox
                                    label="Fed/State Lease #"
                                    name="fedStateLeaseNumber"
                                    register={props.register}
                                    type="number"
                                    errors={props.errors}
                                    bodyStyle={{ width: '50%' }}
                                    style={{ width: '100%' }}
                                />
                                <RHFInputBox
                                    label="Lessor AKA"
                                    name="lessorAka"
                                    register={props.register}
                                    errors={props.errors}
                                    disabled={props.isInHanded}
                                    bodyStyle={{ width: '50%', marginRight: '1rem' }}
                                    style={{ width: '100%' }}
                                />
                            </div>

                            <RHFLegend title="Battery Storage Hold" />
                            <div hidden={true}>
                                <RHFInputBox
                                    label=""
                                    name="batteryStorageHolds[0].id"
                                    register={props.register}
                                    type="number"
                                    disabled={true}
                                    errors={props.errors}
                                />
                                <RHFInputBox
                                    label=""
                                    name="batteryStorageHolds[0].batteryStorageId"
                                    register={props.register}
                                    type="number"
                                    disabled={true}
                                    errors={props.errors}
                                />
                            </div>

                            <div className="form-row">
                                <RHFInputBox
                                    label="Execution Bonus"
                                    name="batteryStorageHolds[0].executionBonus"
                                    register={props.register}
                                    type="number"
                                    errors={props.errors}
                                    step={0.0001}
                                    disabled={props.isInHanded}
                                    onChange={handleTotals}
                                />
                                <RHFSelect
                                    name="batteryStorageHolds[0].executionBonusTypeId"
                                    label=""
                                    options={getOptions('Fee', 'acre')}
                                    register={props.register}
                                    errors={props.errors}
                                    disabled={props.isInHanded}
                                    onChange={handleTotals}
                                />
                                <RHFInputBox
                                    label="Execution Bonus Total"
                                    name="batteryStorageHolds[0].executionBonusTotal"
                                    register={props.register}
                                    type="number"
                                    errors={props.errors}
                                    step={0.0001}
                                    disabled={true}
                                />
                            </div>

                            <div className="form-row">
                                <RHFCheckbox
                                    name="batteryStorageHolds[0].constructionPeriod"
                                    label="Construction Period?"
                                    register={props.register}
                                    errors={props.errors}
                                    onClick={() => onUpdateFields(false)}
                                    disabled={props.isInHanded}
                                />
                            </div>

                            {isConstructionPeriod && (
                                <>
                                    <div className="form-row">
                                        <RHFInputBox
                                            label="Construction Fee"
                                            name="batteryStorageHolds[0].constructionFee"
                                            register={props.register}
                                            type="number"
                                            errors={props.errors}
                                            step={0.0001}
                                            disabled={props.isInHanded}
                                        />
                                        <RHFSelect
                                            name="batteryStorageHolds[0].constructionFeeTypeId"
                                            label=""
                                            options={getOptions('Fee', 'linear foot')}
                                            register={props.register}
                                            errors={props.errors}
                                            disabled={props.isInHanded}
                                        />
                                    </div>
                                    <div className="form-row" style={{ width: '100%' }}>
                                        <RHFLegend title="Construction Details" />
                                        <div className="form-field" style={{ width: '100%' }}>
                                            <textarea
                                                name="batteryStorageHolds[0].constructionDetails"
                                                ref={props.register}
                                                style={{
                                                    width: '100%',
                                                    height: '6rem',
                                                    border: '0',
                                                    borderBottom: '1px solid black',
                                                    resize: 'none',
                                                    overflowY: 'auto',
                                                }}
                                                disabled={props.isInHanded}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="form-row">
                                <RHFCheckbox
                                    name="batteryStorageHolds[0].constructionExtensionPeriod"
                                    label="Construction Extension Period?"
                                    register={props.register}
                                    errors={props.errors}
                                    onClick={() => onUpdateFields(false)}
                                    disabled={props.isInHanded}
                                />
                            </div>

                            {isConstructionExtensionPeriod && (
                                <>
                                    <div className="form-row">
                                        <RHFInputBox
                                            label="Construction Extension Fee"
                                            name="batteryStorageHolds[0].constructionExtensionFee"
                                            register={props.register}
                                            type="number"
                                            errors={props.errors}
                                            step={0.0001}
                                            disabled={props.isInHanded}
                                        />
                                        <RHFSelect
                                            name="batteryStorageHolds[0].constructionExtensionFeeTypeId"
                                            label=""
                                            options={getOptions('Fee', 'linear foot')}
                                            register={props.register}
                                            errors={props.errors}
                                            disabled={props.isInHanded}
                                        />
                                    </div>
                                    <div className="form-row" style={{ width: '100%' }}>
                                        <RHFLegend title="Construction Extension Details" />
                                        <div className="form-field" style={{ width: '100%' }}>
                                            <textarea
                                                name="batteryStorageHolds[0].constructionExtensionDetails"
                                                ref={props.register}
                                                style={{
                                                    width: '100%',
                                                    height: '6rem',
                                                    border: '0',
                                                    borderBottom: '1px solid black',
                                                    resize: 'none',
                                                    overflowY: 'auto',
                                                }}
                                                disabled={props.isInHanded}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="form-row">
                                <RHFCheckbox
                                    name="batteryStorageHolds[0].accessEasement"
                                    label="Access Easement?"
                                    register={props.register}
                                    errors={props.errors}
                                    onClick={() => onUpdateFields(false)}
                                    disabled={props.isInHanded}
                                />
                            </div>

                            {isEasementAccess && (
                                <>
                                    <div className="form-row">
                                        <RHFInputBox
                                            label="Access Fee"
                                            name="batteryStorageHolds[0].accessFee"
                                            register={props.register}
                                            type="number"
                                            errors={props.errors}
                                            step={0.0001}
                                            disabled={props.isInHanded}
                                        />
                                        <RHFSelect
                                            name="batteryStorageHolds[0].accessFeeTypeId"
                                            label=""
                                            options={getOptions('Fee', 'linear foot')}
                                            register={props.register}
                                            errors={props.errors}
                                            disabled={props.isInHanded}
                                        />
                                        <RHFInputBox
                                            label="Access Width"
                                            name="batteryStorageHolds[0].accessWidth"
                                            register={props.register}
                                            type="number"
                                            errors={props.errors}
                                            step={0.0001}
                                            disabled={props.isInHanded}
                                        />
                                        <RHFSelect
                                            name="batteryStorageHolds[0].accessWidthTypeId"
                                            label=""
                                            options={getOptions('Length')}
                                            register={props.register}
                                            errors={props.errors}
                                            disabled={props.isInHanded}
                                        />
                                    </div>
                                    <div className="form-row" style={{ width: '100%' }}>
                                        <RHFLegend title="Access Details" />
                                        <div className="form-field" style={{ width: '100%' }}>
                                            <textarea
                                                name="batteryStorageHolds[0].accessDetails"
                                                ref={props.register}
                                                style={{
                                                    width: '100%',
                                                    height: '6rem',
                                                    border: '0',
                                                    borderBottom: '1px solid black',
                                                    resize: 'none',
                                                    overflowY: 'auto',
                                                }}
                                                disabled={props.isInHanded}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="form-row">
                                <RHFCheckbox
                                    name="batteryStorageHolds[0].transmissionEasement"
                                    label="Transmission Easement?"
                                    register={props.register}
                                    errors={props.errors}
                                    onClick={() => onUpdateFields(false)}
                                    disabled={props.isInHanded}
                                />
                            </div>
                            {isEasementTransmission && (
                                <>
                                    <div className="form-row">
                                        <RHFInputBox
                                            label="Transmission Fee"
                                            name="batteryStorageHolds[0].transmissionFee"
                                            register={props.register}
                                            type="number"
                                            errors={props.errors}
                                            step={0.0001}
                                            disabled={props.isInHanded}
                                        />
                                        <RHFSelect
                                            name="batteryStorageHolds[0].transmissionFeeTypeId"
                                            label=""
                                            options={getOptions('Fee', 'linear foot')}
                                            register={props.register}
                                            errors={props.errors}
                                            disabled={props.isInHanded}
                                        />
                                        <RHFInputBox
                                            label="Transmission Width"
                                            name="batteryStorageHolds[0].transmissionWidth"
                                            register={props.register}
                                            type="number"
                                            errors={props.errors}
                                            step={0.0001}
                                            disabled={props.isInHanded}
                                        />
                                        <RHFSelect
                                            name="batteryStorageHolds[0].transmissionWidthTypeId"
                                            label=""
                                            options={getOptions('Length')}
                                            register={props.register}
                                            errors={props.errors}
                                            disabled={props.isInHanded}
                                        />
                                    </div>
                                    <div className="form-row" style={{ width: '100%' }}>
                                        <RHFLegend title="Transmission Details" />
                                        <div className="form-field" style={{ width: '100%' }}>
                                            <textarea
                                                name="batteryStorageHolds[0].transmissionDetails"
                                                ref={props.register}
                                                style={{
                                                    width: '100%',
                                                    height: '6rem',
                                                    border: '0',
                                                    borderBottom: '1px solid black',
                                                    resize: 'none',
                                                    overflowY: 'auto',
                                                }}
                                                disabled={props.isInHanded}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="form-row">
                                <RHFCheckbox
                                    name="batteryStorageHolds[0].temporaryUseAreas"
                                    label="Temporary Use Areas?"
                                    register={props.register}
                                    errors={props.errors}
                                    onClick={() => onUpdateFields(false)}
                                    disabled={props.isInHanded}
                                />
                            </div>
                            {isTemporaryUse && (
                                <>
                                    <div className="form-row">
                                        <RHFInputBox
                                            label="Temporary Use Fee"
                                            name="batteryStorageHolds[0].temporaryUseFee"
                                            register={props.register}
                                            type="number"
                                            errors={props.errors}
                                            step={0.0001}
                                            disabled={props.isInHanded}
                                        />
                                        <RHFSelect
                                            name="batteryStorageHolds[0].temporaryUseFeeTypeId"
                                            label=""
                                            options={getOptions('Fee', 'linear foot')}
                                            register={props.register}
                                            errors={props.errors}
                                            disabled={props.isInHanded}
                                        />
                                    </div>
                                    <div className="form-row" style={{ width: '100%' }}>
                                        <RHFLegend title="Temporary Use Details" />
                                        <div className="form-field" style={{ width: '100%' }}>
                                            <textarea
                                                name="batteryStorageHolds[0].temporaryUseDetails"
                                                ref={props.register}
                                                style={{
                                                    width: '100%',
                                                    height: '6rem',
                                                    border: '0',
                                                    borderBottom: '1px solid black',
                                                    resize: 'none',
                                                    overflowY: 'auto',
                                                }}
                                                disabled={props.isInHanded}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="form-row">
                                <RHFCheckbox
                                    name="batteryStorageHolds[0].cropBuyout"
                                    label="Crop Buyout?"
                                    register={props.register}
                                    errors={props.errors}
                                    onClick={() => onUpdateFields(false)}
                                    disabled={props.isInHanded}
                                />
                            </div>
                            {isCropBuyout && (
                                <>
                                    <div className="form-row">
                                        <RHFInputBox
                                            label="Crop Buyout Fee"
                                            name="batteryStorageHolds[0].cropBuyoutFee"
                                            register={props.register}
                                            type="number"
                                            errors={props.errors}
                                            step={0.0001}
                                            disabled={props.isInHanded}
                                        />
                                        <RHFSelect
                                            name="batteryStorageHolds[0].cropBuyoutFeeTypeId"
                                            label=""
                                            options={getOptions('Fee', 'linear foot')}
                                            register={props.register}
                                            errors={props.errors}
                                            disabled={props.isInHanded}
                                        />
                                    </div>
                                    <div className="form-row" style={{ width: '100%' }}>
                                        <RHFLegend title="Crop Buyout Details" />
                                        <div className="form-field" style={{ width: '100%' }}>
                                            <textarea
                                                name="batteryStorageHolds[0].cropBuyoutDetails"
                                                ref={props.register}
                                                style={{
                                                    width: '100%',
                                                    height: '6rem',
                                                    border: '0',
                                                    borderBottom: '1px solid black',
                                                    resize: 'none',
                                                    overflowY: 'auto',
                                                }}
                                                disabled={props.isInHanded}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="form-row">
                                <RHFCheckbox
                                    name="batteryStorageHolds[0].developmentPeriod"
                                    label="Development Period?"
                                    register={props.register}
                                    errors={props.errors}
                                    onClick={() => onUpdateFields(false)}
                                    disabled={props.isInHanded}
                                />
                            </div>
                            {isDevelopmentPeriod && (
                                <div className="form-row">
                                    <RHFInputBox
                                        label="Development Period Term"
                                        name="batteryStorageHolds[0].developmentPeriodTerm"
                                        register={props.register}
                                        type="number"
                                        errors={props.errors}
                                        disabled={props.isInHanded}
                                    />
                                    <RHFSelect
                                        name="batteryStorageHolds[0].developmentPeriodTermTypeId"
                                        label=""
                                        options={getOptions('Time')}
                                        register={props.register}
                                        errors={props.errors}
                                        disabled={props.isInHanded}
                                    />
                                    <RHFInputBox
                                        label="Development Rent Amount"
                                        name="batteryStorageHolds[0].developmentRentAmount"
                                        register={props.register}
                                        type="number"
                                        errors={props.errors}
                                        step={0.0001}
                                        disabled={props.isInHanded}
                                    />
                                    <RHFSelect
                                        name="batteryStorageHolds[0].developmentRentTermTypeId"
                                        label=""
                                        options={getOptions('Fee', 'acre')}
                                        register={props.register}
                                        errors={props.errors}
                                        disabled={props.isInHanded}
                                    />
                                </div>
                            )}

                            <div className="form-row">
                                <RHFCheckbox
                                    name="batteryStorageHolds[0].developmentPeriodExtension"
                                    label="Development Period Extension?"
                                    register={props.register}
                                    errors={props.errors}
                                    onClick={() => onUpdateFields(false)}
                                    disabled={props.isInHanded}
                                />
                            </div>
                            {isDevelopmentPeriodExtension && (
                                <div className="form-row">
                                    <RHFInputBox
                                        label="Extension Term"
                                        name="batteryStorageHolds[0].developmentPeriodExtensionTerm"
                                        register={props.register}
                                        type="number"
                                        errors={props.errors}
                                        disabled={props.isInHanded}
                                    />
                                    <RHFSelect
                                        name="batteryStorageHolds[0].developmentPeriodExtensionTermTypeId"
                                        label=""
                                        options={getOptions('Time')}
                                        register={props.register}
                                        errors={props.errors}
                                        disabled={props.isInHanded}
                                    />
                                    <RHFInputBox
                                        label="Extension Rent Amount"
                                        name="batteryStorageHolds[0].firstExtensionOptionRentAmount"
                                        register={props.register}
                                        type="number"
                                        errors={props.errors}
                                        step={0.0001}
                                        disabled={props.isInHanded}
                                    />
                                    <RHFSelect
                                        name="batteryStorageHolds[0].firstExtensionOptionRentTermTypeId"
                                        label=""
                                        options={getOptions('Fee', 'acre')}
                                        register={props.register}
                                        errors={props.errors}
                                        disabled={props.isInHanded}
                                    />
                                    <RHFCheckbox
                                        label="Prepaid Option"
                                        name="batteryStorageHolds[0].prepaidOption"
                                        register={props.register}
                                        errors={props.errors}
                                        bodyStyle={{ width: '150px', display: 'flex', alignItems: 'center', gap: '1rem' }}
                                        disabled={props.isInHanded}
                                    />
                                </div>
                            )}

                            <div className="form-row">
                                <RHFCheckbox
                                    name="batteryStorageHolds[0].developmentPeriodSecondExtension"
                                    label="Development Period Second Extension?"
                                    register={props.register}
                                    errors={props.errors}
                                    onClick={() => onUpdateFields(false)}
                                    disabled={props.isInHanded}
                                />
                            </div>
                            {isDevelopmentPeriodSecondExtension && (
                                <div className="form-row">
                                    <RHFInputBox
                                        label="Second Extension Term"
                                        name="batteryStorageHolds[0].developmentPeriodSecondExtensionTerm"
                                        register={props.register}
                                        type="number"
                                        errors={props.errors}
                                        disabled={props.isInHanded}
                                    />
                                    <RHFSelect
                                        name="batteryStorageHolds[0].developmentPeriodSecondExtensionTermTypeId"
                                        label=""
                                        options={getOptions('Time')}
                                        register={props.register}
                                        errors={props.errors}
                                        disabled={props.isInHanded}
                                    />
                                    <RHFInputBox
                                        label="Second Extension Rent Amount"
                                        name="batteryStorageHolds[0].secondExtensionOptionRentAmount"
                                        register={props.register}
                                        type="number"
                                        errors={props.errors}
                                        step={0.0001}
                                        disabled={props.isInHanded}
                                    />
                                    <RHFSelect
                                        name="batteryStorageHolds[0].secondExtensionOptionRentTermTypeId"
                                        label=""
                                        options={getOptions('Fee', 'acre')}
                                        register={props.register}
                                        errors={props.errors}
                                        disabled={props.isInHanded}
                                    />
                                </div>
                            )}

                            {/* TODO: Investigate possibility of adding an Extensions table for Battery Storage */}

                            <div className="form-row">
                                <RHFCheckbox
                                    name="batteryStorageHolds[0].operationsPeriod"
                                    label="Operations Period?"
                                    register={props.register}
                                    errors={props.errors}
                                    onClick={() => onUpdateFields(false)}
                                    disabled={props.isInHanded}
                                />
                            </div>
                            {isOperationsPeriod && (
                                <>
                                    <div className="form-row">
                                        <RHFInputBox
                                            label="Operations Term"
                                            name="batteryStorageHolds[0].operationsPeriodTerm"
                                            register={props.register}
                                            type="number"
                                            errors={props.errors}
                                            disabled={props.isInHanded}
                                        />
                                        <RHFSelect
                                            name="batteryStorageHolds[0].operationsPeriodTermTypeId"
                                            label=""
                                            options={getOptions('Time')}
                                            register={props.register}
                                            errors={props.errors}
                                            disabled={props.isInHanded}
                                        />
                                        <RHFInputBox
                                            label="Base Operations Rent Amount"
                                            name="batteryStorageHolds[0].operationsBaseRentAmount"
                                            register={props.register}
                                            type="number"
                                            errors={props.errors}
                                            step={0.0001}
                                            disabled={props.isInHanded}
                                        />
                                        <RHFSelect
                                            name="batteryStorageHolds[0].operationsBaseRentTermTypeId"
                                            label=""
                                            options={getOptions('Fee', 'acre')}
                                            register={props.register}
                                            errors={props.errors}
                                            disabled={props.isInHanded}
                                        />
                                    </div>

                                    <div className="form-row">
                                        <RHFInputBox
                                            label="Annual Escalation Rate (Use decimal format)"
                                            name="batteryStorageHolds[0].annualEscalationRate"
                                            register={props.register}
                                            type="number"
                                            errors={props.errors}
                                            step={0.0001}
                                            disabled={props.isInHanded}
                                        />
                                        <RHFCheckbox
                                            name="batteryStorageHolds[0].escalationAbovePrime"
                                            label="Above Prime Rate?"
                                            register={props.register}
                                            errors={props.errors}
                                            onClick={() => onUpdateFields(false)}
                                            disabled={props.isInHanded}
                                        />
                                    </div>

                                    <div className="form-row">
                                        <RHFInputBox
                                            label="Annual Overdue Rate (Use decimal format)"
                                            name="batteryStorageHolds[0].overdueRateTerm"
                                            register={props.register}
                                            type="number"
                                            errors={props.errors}
                                            step={0.0001}
                                            disabled={props.isInHanded}
                                        />
                                        <RHFCheckbox
                                            name="batteryStorageHolds[0].overdueAbovePrime"
                                            label="Above Prime Rate?"
                                            register={props.register}
                                            errors={props.errors}
                                            onClick={() => onUpdateFields(false)}
                                            disabled={props.isInHanded}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="form-row">
                                <RHFCheckbox
                                    name="batteryStorageHolds[0].extendedOperationsPeriod"
                                    label="Extended Operations Period?"
                                    register={props.register}
                                    errors={props.errors}
                                    onClick={() => onUpdateFields(false)}
                                    disabled={props.isInHanded}
                                />
                            </div>
                            {isExtendedOperationsPeriod && (
                                <div className="form-row">
                                    <RHFInputBox
                                        label="Extended Operations Term"
                                        name="batteryStorageHolds[0].extendedOperationsPeriodTerm"
                                        register={props.register}
                                        type="number"
                                        errors={props.errors}
                                        disabled={props.isInHanded}
                                    />
                                    <RHFSelect
                                        name="batteryStorageHolds[0].extendedOperationsPeriodTermTypeId"
                                        label=""
                                        options={getOptions('Time')}
                                        register={props.register}
                                        errors={props.errors}
                                        disabled={props.isInHanded}
                                    />
                                    <RHFInputBox
                                        label="Extended Operations Rent Amount"
                                        name="batteryStorageHolds[0].extendedOperationsRentAmount"
                                        register={props.register}
                                        type="number"
                                        errors={props.errors}
                                        step={0.0001}
                                        disabled={props.isInHanded}
                                    />
                                    <RHFSelect
                                        name="batteryStorageHolds[0].extendedOperationsRentTermTypeId"
                                        label=""
                                        options={getOptions('Fee', 'acre')}
                                        register={props.register}
                                        errors={props.errors}
                                        disabled={props.isInHanded}
                                    />
                                </div>
                            )}

                            <div className="form-row">
                                <RHFCheckbox
                                    name="batteryStorageHolds[0].restorationPeriod"
                                    label="Restoration Period?"
                                    register={props.register}
                                    errors={props.errors}
                                    onClick={() => onUpdateFields(false)}
                                    disabled={props.isInHanded}
                                />
                            </div>
                            {isRestorationPeriod && (
                                <div className="form-row">
                                    <RHFInputBox
                                        label="Restoration Term"
                                        name="batteryStorageHolds[0].restorationTerm"
                                        register={props.register}
                                        type="number"
                                        errors={props.errors}
                                        disabled={props.isInHanded}
                                    />
                                    <RHFSelect
                                        name="batteryStorageHolds[0].restorationTermTypeId"
                                        label=""
                                        options={getOptions('Time')}
                                        register={props.register}
                                        errors={props.errors}
                                        disabled={props.isInHanded}
                                    />
                                    <RHFInputBox
                                        label="Restoration Rent Amount"
                                        name="batteryStorageHolds[0].restorationTermRentAmount"
                                        register={props.register}
                                        type="number"
                                        errors={props.errors}
                                        step={0.0001}
                                        disabled={props.isInHanded}
                                    />
                                    <RHFSelect
                                        name="batteryStorageHolds[0].restorationTermRentTypeId"
                                        label=""
                                        options={getOptions('Fee', 'acre')}
                                        register={props.register}
                                        errors={props.errors}
                                        disabled={props.isInHanded}
                                    />
                                </div>
                            )}

                            <div className="form-row">
                                <RHFCheckbox
                                    name="batteryStorageHolds[0].substationArea"
                                    label="Substation Area?"
                                    register={props.register}
                                    errors={props.errors}
                                    onClick={() => onUpdateFields(false)}
                                    disabled={props.isInHanded}
                                />
                            </div>
                            {isSubstationArea && (
                                <>
                                    <div className="form-row">
                                        <RHFInputBox
                                            label="Substation Acres"
                                            name="batteryStorageHolds[0].substationAcres"
                                            register={props.register}
                                            type="number"
                                            errors={props.errors}
                                            step={0.0001}
                                            disabled={props.isInHanded}
                                            onChange={handleTotals}
                                        />
                                        <RHFInputBox
                                            label="Substation Fee"
                                            name="batteryStorageHolds[0].substationFee"
                                            register={props.register}
                                            type="number"
                                            errors={props.errors}
                                            step={0.0001}
                                            disabled={props.isInHanded}
                                            onChange={handleTotals}
                                        />
                                        <RHFSelect
                                            name="batteryStorageHolds[0].substationFeeTypeId"
                                            label=""
                                            options={getOptions('Fee', 'acre')}
                                            register={props.register}
                                            errors={props.errors}
                                            disabled={props.isInHanded}
                                            onChange={handleTotals}
                                        />
                                        <RHFInputBox
                                            label="Substation Fee Total"
                                            name="batteryStorageHolds[0].substationFeeTotal"
                                            register={props.register}
                                            type="number"
                                            errors={props.errors}
                                            step={0.0001}
                                            disabled={props.isInHanded}
                                        />
                                    </div>
                                    <div className="form-row" style={{ width: '100%' }}>
                                        <RHFLegend title="Substation Details" />
                                        <div className="form-field" style={{ width: '100%' }}>
                                            <textarea
                                                name="batteryStorageHolds[0].substation"
                                                ref={props.register}
                                                style={{
                                                    width: '100%',
                                                    height: '6rem',
                                                    border: '0',
                                                    borderBottom: '1px solid black',
                                                    resize: 'none',
                                                    overflowY: 'auto',
                                                }}
                                                disabled={props.isInHanded}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="form-row">
                                <RHFCheckbox
                                    name="batteryStorageHolds[0].rightsToAssign"
                                    label="Rights To Assign?"
                                    register={props.register}
                                    errors={props.errors}
                                    onClick={() => onUpdateFields(false)}
                                    disabled={props.isInHanded}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BatteryStorageGeneration;



// WEBPACK FOOTER //
// ./src/components/project/sections/LeasingComponents/redesign/BatteryStorageComponents/BatteryStorageGeneration.tsx